/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: TenantFormFields.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Form fields component for Tenant Information tab.
────────────────────────────────────────────────────────────────*/

import { Building, Phone } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import { FormBox } from '../../../components/ui/FormBox';
import Toggle from '../../../components/ui/Toggle';
import TimezoneSelect from '../../../components/ui/TimezoneSelect';
import LanguageSelect from '../../../components/ui/LanguageSelect';
import type { TenantFormState } from '../hooks/useTenantData';

interface TenantFormFieldsProps {
  formData: TenantFormState;
  errors: Record<string, string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onInputChange: (field: string, value: any) => void;
  isPrimary?: boolean;
}

export function TenantFormFields({
  formData,
  errors,
  onInputChange,
  isPrimary = false
}: TenantFormFieldsProps) {
  const { t } = useI18n();

  return (
    <>
      {/* Basic Information */}
      <FormBox title={t('tenants.basicInformation')} icon={<Building />}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
          {/* Row 1 - Tenant Name and Description */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">
              {t('tenants.tenantName')} *
            </label>
          </div>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => onInputChange('name', e.target.value)}
            className={`px-3 py-2 text-sm border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent ${errors.name ? 'border-red-500' : 'border-border'
              }`}
            placeholder={t('tenants.placeholders.name')}
          />
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">
              {t('tenants.description')}
            </label>
          </div>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => onInputChange('description', e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder={t('tenants.placeholders.description')}
          />

          {/* Row 2 - Time Zone and Admin Email */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">
              {t('tenants.timezone')} *
            </label>
          </div>
          <TimezoneSelect
            value={formData.timezone}
            onChange={(value) => onInputChange('timezone', value)}
            placeholder="Select timezone..."
          />
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">
              {t('tenants.adminEmail')}
            </label>
          </div>
          <input
            type="email"
            value={formData.adminEmail}
            onChange={(e) => onInputChange('adminEmail', e.target.value)}
            className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            placeholder={t('tenants.placeholders.adminEmail')}
          />

          {/* Row 3 - Localization and Status */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">
              {t('tenants.defaultLocale')} *
            </label>
          </div>
          <LanguageSelect
            value={formData.defaultLocale}
            onChange={(value) => onInputChange('defaultLocale', value)}
            placeholder="Select language..."
          />
          {/* Enabled - Read-only for Primary Tenant (cannot be disabled) */}
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">
              {t('tenants.enabled')}
            </label>
          </div>
          <div className="flex items-center">
            <Toggle
              value={formData.isActive}
              onChange={(value) => onInputChange('isActive', value)}
              label=""
              disabled={isPrimary}
            />
          </div>
          <div></div>
          <div></div>
        </div>
      </FormBox>

      {/* Telephony Privileges - Hidden for Primary Tenant */}
      {!isPrimary && (
        <FormBox title={t('tenants.privileges')} icon={<Phone />}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
            {/* Row 5 - Max Extensions and Max Trunks */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                {t('tenants.maxExtensions')}
              </label>
            </div>
            <input
              type="number"
              min="0"
              value={formData.maxExtensions}
              onChange={(e) => onInputChange('maxExtensions', parseInt(e.target.value) || 0)}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            />
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                {t('tenants.maxTrunks')}
              </label>
            </div>
            <input
              type="number"
              min="0"
              value={formData.maxTrunks}
              onChange={(e) => onInputChange('maxTrunks', parseInt(e.target.value) || 0)}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            />

            {/* Row 6 - Max Queues and Max IVR */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                {t('tenants.maxQueues')}
              </label>
            </div>
            <input
              type="number"
              min="0"
              value={formData.maxQueues}
              onChange={(e) => onInputChange('maxQueues', parseInt(e.target.value) || 0)}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            />
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                {t('tenants.maxIvr')}
              </label>
            </div>
            <input
              type="number"
              min="0"
              value={formData.maxIvr}
              onChange={(e) => onInputChange('maxIvr', parseInt(e.target.value) || 0)}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            />

            {/* Row 7 - Max Conferences and Max Parking Lots */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                {t('tenants.maxConferences')}
              </label>
            </div>
            <input
              type="number"
              min="0"
              value={formData.maxConferences}
              onChange={(e) => onInputChange('maxConferences', parseInt(e.target.value) || 0)}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            />
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                {t('tenants.maxParkingLots')}
              </label>
            </div>
            <input
              type="number"
              min="0"
              value={formData.maxParkingLots}
              onChange={(e) => onInputChange('maxParkingLots', parseInt(e.target.value) || 0)}
              className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
            />

            {/* Row 8 - Allow Recording */}
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                {t('tenants.allowRecording')}
              </label>
            </div>
            <div className="flex items-center">
              <Toggle
                value={formData.allowRecording}
                onChange={(value) => onInputChange('allowRecording', value)}
                label=""
              />
            </div>
            <div></div>
          </div>
        </FormBox>
      )}
    </>
  );
}

